import { z } from "zod";

/**
 * Hub user types for blacklist management
 */

/**
 * Input schema for saving a blacklist address
 * Hub user ID is automatically resolved from the DeFi address
 */
export const SaveBlacklistAddressDtoSchema = z.object({
  defiAddress: z.string().min(1),
});
export type SaveBlacklistAddressDto = z.infer<typeof SaveBlacklistAddressDtoSchema>;

export const SaveBlacklistAddressesDtoSchema = z.object({
  addresses: z.array(SaveBlacklistAddressDtoSchema),
});
export type SaveBlacklistAddressesDto = z.infer<typeof SaveBlacklistAddressesDtoSchema>;

/**
 * Blacklist address DTO schema (response)
 */
export const BlacklistAddressDtoSchema = z.object({
  id: z.uuid(),
  defiAddress: z.string(),
  createdAt: z.string(),
});
export type BlacklistAddressDto = z.infer<typeof BlacklistAddressDtoSchema>;

/**
 * Pagination parameters for listing blacklist addresses
 */
export const ListBlacklistAddressesParamsSchema = z.object({
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  search: z.string().optional(),
});
export type ListBlacklistAddressesParams = z.infer<typeof ListBlacklistAddressesParamsSchema>;

/**
 * Paginated response for listing blacklist addresses
 */
export const ListBlacklistAddressesResponseSchema = z.object({
  items: z.array(BlacklistAddressDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export type ListBlacklistAddressesResponse = z.infer<typeof ListBlacklistAddressesResponseSchema>;

/**
 * Input schema for batch removing blacklist addresses by defi address
 */
export const RemoveBatchBlacklistAddressesDtoSchema = z.object({
  defiAddresses: z.array(z.string().min(1)),
});
export type RemoveBatchBlacklistAddressesDto = z.infer<typeof RemoveBatchBlacklistAddressesDtoSchema>;

/**
 * Hub user types for whitelist management
 */

/**
 * Input schema for saving a whitelist address
 * Hub user ID is automatically resolved from the DeFi address
 */
export const SaveWhitelistAddressDtoSchema = z.object({
  defiAddress: z.string().min(1),
});
export type SaveWhitelistAddressDto = z.infer<typeof SaveWhitelistAddressDtoSchema>;

export const SaveWhitelistAddressesDtoSchema = z.object({
  addresses: z.array(SaveWhitelistAddressDtoSchema),
});
export type SaveWhitelistAddressesDto = z.infer<typeof SaveWhitelistAddressesDtoSchema>;

/**
 * Whitelist address DTO schema (response)
 */
export const WhitelistAddressDtoSchema = z.object({
  id: z.uuid(),
  defiAddress: z.string(),
  createdAt: z.string(),
});
export type WhitelistAddressDto = z.infer<typeof WhitelistAddressDtoSchema>;

/**
 * Pagination parameters for listing whitelist addresses
 */
export const ListWhitelistAddressesParamsSchema = z.object({
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  search: z.string().optional(),
});
export type ListWhitelistAddressesParams = z.infer<typeof ListWhitelistAddressesParamsSchema>;

/**
 * Paginated response for listing whitelist addresses
 */
export const ListWhitelistAddressesResponseSchema = z.object({
  items: z.array(WhitelistAddressDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export type ListWhitelistAddressesResponse = z.infer<typeof ListWhitelistAddressesResponseSchema>;

/**
 * Admin Hub User types
 */

export const AdminHubUserDtoSchema = z.object({
  id: z.number(),
  /** Null for email-OTP users who never linked a wallet. */
  defiAddress: z.string().nullable(),
  /** Null for wallet-only users. */
  email: z.string().nullable(),
  /** Arena display name ("username"). */
  nickname: z.string().nullable(),
  /** Arena profile picture CDN URL, null when unset. */
  avatarUrl: z.string().nullable(),
  /** Arena profile quote, null when unset. */
  quote: z.string().nullable(),
  createdAt: z.string(),
  ambassadorName: z.string().nullable(),
  isAmbassador: z.boolean(),
});
export type AdminHubUserDto = z.infer<typeof AdminHubUserDtoSchema>;

export const ListAdminHubUsersResponseSchema = z.object({
  items: z.array(AdminHubUserDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export type ListAdminHubUsersResponse = z.infer<typeof ListAdminHubUsersResponseSchema>;

export const UpdateAdminHubUserDtoSchema = z.object({
  id: z.number(),
  ambassadorName: z.string().nullable(),
  isAmbassador: z.boolean(),
});
export type UpdateAdminHubUserDto = z.infer<typeof UpdateAdminHubUserDtoSchema>;
